/**
 * useApiRequest - A hook for managing API requests with loading and error states
 *
 * This hook provides a standardized way to manage API requests with:
 * - Loading state tracking
 * - Error handling
 * - Request execution
 *
 * @returns An object containing the request state and execution function
 */
import { useState, useCallback } from "react";
import { NetworkError } from "./Errors";
export function useApiRequest() {
    const [state, setState] = useState({
        data: null,
        isLoading: false,
        error: null,
    });
    const execute = useCallback(async (requestFn, params) => {
        setState((prevState) => ({
            ...prevState,
            isLoading: true,
            error: null,
        }));
        try {
            const response = await requestFn(params);
            setState({
                data: response,
                isLoading: false,
                error: null,
            });
            return response;
        }
        catch (error) {
            const formattedError = error instanceof Error
                ? error
                : new NetworkError("Unknown error occurred");
            setState({
                data: null,
                isLoading: false,
                error: formattedError,
            });
            throw formattedError;
        }
    }, []);
    const reset = useCallback(() => {
        setState({
            data: null,
            isLoading: false,
            error: null,
        });
    }, []);
    return {
        state,
        execute,
        reset,
    };
}
